import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import { useAuditorCohortIds } from '@/hooks/useAuditorCohortIds';

// Types
export interface UserSubscription {
  plan_id: string;
  plan_code: string;
  plan_type: string;
  status: string;
  provider: string | null;
  livemode: boolean | null;
  current_period_start: string | null;
  current_period_end: string | null;
}

export interface ManagedUser {
  id: string;
  full_name: string;
  email: string;
  phone: string | null;
  avatar_url: string | null;
  company: string | null;
  position: string | null;
  area: string | null;
  organization_id: string | null;
  status: string | null;
  last_login: string | null;
  created_at: string;
  organization_name: string | null;
  roles: string[];
  cohorts: { id: string; name: string }[];
  subscriptions: UserSubscription[];
  has_enterprise_license: boolean;
  has_pro_subscription: boolean;
}

export interface Organization {
  id: string;
  name: string;
  sector: string | null;
  status: string | null;
  max_users: number | null;
}

export interface Cohort {
  id: string;
  name: string;
  status: string | null;
  start_date: string | null;
  end_date: string | null;
  organization_id: string | null;
  program_id?: string;
  is_public?: boolean;
  program_name?: string | null;
  organization_name?: string | null;
}


export interface BusinessArea {
  id: string;
  name: string;
}

export interface AuditLog {
  id: string;
  action: string;
  description: string | null;
  metadata: any;
  created_at: string;
  actor_user_id: string;
  organization_id: string | null;
  actor_name?: string;
}

// Enterprise plan ID constant
export const ENTERPRISE_PLAN_ID = '786a666b-1b3a-40c5-8ee9-54de870d52c6';
export const PRO_PLAN_ID = '8595276a-eab1-432f-828f-0a92d35c6a27';

async function invokeManageUsers(body: Record<string, unknown>) {
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;

  const { data, error } = await supabase.functions.invoke('manage-users', {
    body,
    ...(accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : {}),
  });

  if (error) {
    // FunctionsHttpError expone la Response cruda en context; extraer mensaje real
    const ctx = (error as any).context;
    if (ctx && typeof ctx.clone === 'function') {
      try {
        const parsed = await ctx.clone().json();
        const msg = parsed?.error || parsed?.message;
        if (msg) return { data: null, error: new Error(msg) };
      } catch {
        try {
          const text = await ctx.clone().text();
          if (text) return { data: null, error: new Error(text) };
        } catch { /* noop */ }
      }
    }
  }

  return { data, error };
}

export function useUserManagement() {
  const { currentRole, profile } = useAuth();
  const queryClient = useQueryClient();
  const isSuperAdmin = currentRole === 'super_admin';
  const isAdmin = currentRole === 'admin';
  // Auditor: solo lectura, scopeado por cohortes asignadas vía RLS. NO se filtra
  // por su organización (su acceso es por cohorte, no por empresa).
  const isAuditor = currentRole === 'auditor';
  // La RLS del auditor no basta si además tiene otro rol (ej. admin de su
  // empresa): las policies se suman. La vista de auditor se limita aquí a los
  // participantes de sus cohortes asignadas.
  const auditorScope = useAuditorCohortIds();

  // === FETCH USERS ===
  const usersQuery = useQuery({
    queryKey: ['managed-users', currentRole, profile?.organization_id, isAuditor ? `${auditorScope.cohortIds.join(',')}:${auditorScope.participantIds.length}` : null],
    queryFn: async (): Promise<ManagedUser[]> => {
      const PAGE_SIZE = 1000;
      let allProfiles: any[] = [];
      let page = 0;
      while (true) {
        let q = supabase
          .from('profiles')
          .select('id, full_name, email, phone, avatar_url, company, position, area, organization_id, status, last_login, created_at')
          .order('created_at', { ascending: false })
          .order('id') // desempate estable: created_at no es único y la paginación perdería filas
          .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
        if (!isSuperAdmin && !isAuditor && profile?.organization_id) {
          q = q.eq('organization_id', profile.organization_id);
        }
        const { data: pageData, error } = await q;
        if (error) throw error;
        if (!pageData || pageData.length === 0) break;
        allProfiles = allProfiles.concat(pageData);
        if (pageData.length < PAGE_SIZE) break;
        page++;
      }
      const auditorParticipants = new Set(auditorScope.participantIds);
      const profiles = isAuditor
        ? allProfiles.filter((p) => auditorParticipants.has(p.id))
        : allProfiles;
      if (profiles.length === 0) return [];

      const userIds = profiles.map((p) => p.id);

      // Helper: chunk .in() queries to avoid URL length and default row limits
      const chunk = <T,>(arr: T[], size: number): T[][] => {
        const out: T[][] = [];
        for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
        return out;
      };
      const userIdChunks = chunk(userIds, 200);

      const fetchChunked = async <R,>(
        builder: (ids: string[]) => PromiseLike<{ data: R[] | null; error: any }>
      ): Promise<R[]> => {
        const results = await Promise.all(userIdChunks.map(async (ids) => await builder(ids)));
        return results.flatMap((r) => r.data || []);
      };

      const [rolesData, cohortsData, orgsRes, subsData, plansRes] = await Promise.all([
        fetchChunked<any>((ids) =>
          supabase.from('user_roles').select('user_id, role').in('user_id', ids)
        ),
        fetchChunked<any>((ids) =>
          supabase
            .from('cohort_participants')
            .select('user_id, cohort_id, cohorts(id, name, is_perpetual)')
            .in('user_id', ids)
        ),
        supabase.from('organizations').select('id, name'),
        fetchChunked<any>((ids) =>
          supabase
            .from('user_subscriptions')
            .select('user_id, plan_id, status, provider, livemode, current_period_start, current_period_end')
            .in('user_id', ids)
        ),
        supabase.from('billing_plans').select('id, code, plan_type'),
      ]);

      const rolesRes = { data: rolesData };
      const cohortsRes = { data: cohortsData };
      const subsRes = { data: subsData };
      const plansMap = new Map<string, { code: string; plan_type: string }>();
      (plansRes.data || []).forEach((p: any) => plansMap.set(p.id, { code: p.code, plan_type: p.plan_type }));


      const rolesMap = new Map<string, string[]>();
      (rolesRes.data || []).forEach((r: any) => {
        const existing = rolesMap.get(r.user_id) || [];
        existing.push(r.role);
        rolesMap.set(r.user_id, existing);
      });

      const cohortsMap = new Map<string, { id: string; name: string }[]>();
      (cohortsRes.data || []).forEach((cp: any) => {
        const existing = cohortsMap.get(cp.user_id) || [];
        // Las rutas de autoaprendizaje no cuentan como cohorte del usuario.
        // El auditor solo ve sus cohortes asignadas, no las demás del usuario.
        const outOfAuditorScope = isAuditor && !auditorScope.cohortIds.includes(cp.cohort_id);
        if (cp.cohorts && cp.cohorts.is_perpetual !== true && !outOfAuditorScope) {
          existing.push({ id: cp.cohorts.id, name: cp.cohorts.name });
        }
        cohortsMap.set(cp.user_id, existing);
      });

      const orgsMap = new Map<string, string>();
      (orgsRes.data || []).forEach((o: any) => {
        orgsMap.set(o.id, o.name);
      });

      const subsMap = new Map<string, UserSubscription[]>();
      (subsRes.data || []).forEach((s: any) => {
        const existing = subsMap.get(s.user_id) || [];
        const plan = plansMap.get(s.plan_id);
        if (plan) {
          existing.push({
            plan_id: s.plan_id,
            plan_code: plan.code,
            plan_type: plan.plan_type,
            status: s.status,
            provider: s.provider ?? null,
            livemode: s.livemode ?? null,
            current_period_start: s.current_period_start ?? null,
            current_period_end: s.current_period_end ?? null,
          });
        }
        subsMap.set(s.user_id, existing);
      });

      return profiles.map((p) => {
        const subs = subsMap.get(p.id) || [];
        return {
          ...p,
          last_login: p.last_login ?? null,
          organization_name: p.organization_id ? orgsMap.get(p.organization_id) || null : null,
          roles: rolesMap.get(p.id) || [],
          cohorts: cohortsMap.get(p.id) || [],
          subscriptions: subs,
          has_enterprise_license: subs.some((s) => s.plan_type === 'enterprise' && s.status === 'active'),
          has_pro_subscription: subs.some((s) =>
            s.plan_type === 'subscription' &&
            s.status === 'active' &&
            (s.provider !== 'stripe' || s.livemode === true)
          ),
        };
      });
    },
    enabled: isSuperAdmin || isAdmin || (isAuditor && auditorScope.isReady),
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  // === FETCH LAST LOGINS (en segundo plano: no bloquea la tabla) ===
  const lastLoginsQuery = useQuery({
    queryKey: ['managed-users-last-logins'],
    queryFn: async (): Promise<Record<string, string | null>> => {
      const { data, error } = await invokeManageUsers({ action: 'get_last_logins' });
      if (error) throw error;
      return (data as any)?.last_logins || {};
    },
    enabled: isSuperAdmin || isAdmin || isAuditor,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: false,
  });

  const usersWithLastLogin = useMemo(() => {
    const base = usersQuery.data || [];
    const logins = lastLoginsQuery.data;
    if (!logins) return base;
    return base.map((u) => ({ ...u, last_login: logins[u.id] ?? u.last_login ?? null }));
  }, [usersQuery.data, lastLoginsQuery.data]);

  // === FETCH ORGANIZATIONS ===
  const organizationsQuery = useQuery({
    queryKey: ['organizations-list'],
    queryFn: async (): Promise<Organization[]> => {
      const { data, error } = await supabase
        .from('organizations')
        .select('id, name, sector, status, max_users')
        .order('name');
      if (error) throw error;
      return data || [];
    },
    enabled: isSuperAdmin || isAdmin || isAuditor,
  });

  // === FETCH COHORTS (include program_id) ===
  const cohortsQuery = useQuery({
    queryKey: ['cohorts-list'],
    queryFn: async (): Promise<Cohort[]> => {
      const { data, error } = await supabase
        .from('cohorts')
        .select('id, name, status, start_date, end_date, organization_id, program_id, is_public, programs(name), organizations(name)')
        .eq('is_perpetual', false)
        .order('start_date', { ascending: false });
      if (error) throw error;
      return (data || []).map((c: any) => ({
        ...c,
        program_name: c.programs?.name || null,
        organization_name: c.organizations?.name || null,
        programs: undefined,
        organizations: undefined,
      }));
    },
    enabled: isSuperAdmin || isAdmin || isAuditor,
  });


  // === FETCH BUSINESS AREAS ===
  const businessAreasQuery = useQuery({
    queryKey: ['business-areas-list'],
    queryFn: async (): Promise<BusinessArea[]> => {
      const { data, error } = await supabase
        .from('business_areas')
        .select('id, name')
        .order('name');
      if (error) throw error;
      return (data || []) as BusinessArea[];
    },
    enabled: isSuperAdmin || isAdmin || isAuditor,
  });

  // (audit logs are now fetched via the standalone `useAuditLogs` hook below)

  // === CREATE USER MUTATION ===
  const createUserMutation = useMutation({
    mutationFn: async (userData: {
      email: string;
      full_name: string;
      phone?: string;
      role: string;
      organization_id?: string;
      cohort_id?: string;
      position?: string;
      area?: string;
      password?: string;
    }) => {
      const { data, error } = await invokeManageUsers({ action: 'create', ...userData });
      if (error) {
        let serverMsg = error.message;
        try {
          const ctx: any = (error as any).context;
          if (ctx?.body) {
            const parsed = typeof ctx.body === 'string' ? JSON.parse(ctx.body) : ctx.body;
            if (parsed?.error) serverMsg = parsed.error;
          } else if (ctx?.json) {
            const parsed = await ctx.json();
            if (parsed?.error) serverMsg = parsed.error;
          }
        } catch (_) { /* ignore */ }
        throw new Error(serverMsg);
      }
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['managed-users'] });
      queryClient.invalidateQueries({ queryKey: ['audit-logs'] });
      toast({ title: 'Usuario creado', description: 'El usuario ha sido creado exitosamente' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error al crear usuario', description: error.message, variant: 'destructive' });
    },
  });

  // === BATCH CREATE MUTATION ===
  const batchCreateMutation = useMutation({
    mutationFn: async (data: {
      users: { email: string; full_name: string; phone?: string; role?: string; position?: string; area?: string; password?: string }[];
      organization_id?: string;
      cohort_id?: string;
    }) => {
      const { data: result, error } = await invokeManageUsers({ action: 'batch_create', ...data });
      if (error) throw error;
      if (result?.error) throw new Error(result.error);
      return result;
    },
    onSuccess: (data) => {
      const successCount = data.results?.filter((r: any) => r.success).length || 0;
      const total = data.results?.length || 0;
      queryClient.invalidateQueries({ queryKey: ['managed-users'] });
      queryClient.invalidateQueries({ queryKey: ['audit-logs'] });
      toast({
        title: 'Importación completada',
        description: `${successCount}/${total} usuarios creados exitosamente`,
      });
    },
    onError: (error: Error) => {
      toast({ title: 'Error en importación', description: error.message, variant: 'destructive' });
    },
  });

  // === UPDATE STATUS MUTATION ===
  const updateStatusMutation = useMutation({
    mutationFn: async ({ user_id, status }: { user_id: string; status: string }) => {
      const { data, error } = await invokeManageUsers({ action: 'update_status', user_id, status });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['managed-users'] });
      queryClient.invalidateQueries({ queryKey: ['audit-logs'] });
      toast({ title: 'Estado actualizado' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // === UPDATE ROLE MUTATION ===
  const updateRoleMutation = useMutation({
    mutationFn: async ({ user_id, role }: { user_id: string; role: string }) => {
      const { data, error } = await invokeManageUsers({ action: 'update_role', user_id, role });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['managed-users'] });
      queryClient.invalidateQueries({ queryKey: ['audit-logs'] });
      toast({ title: 'Rol actualizado' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // === UPDATE USER MUTATION (full edit) ===
  const updateUserMutation = useMutation({
    mutationFn: async (userData: {
      user_id: string;
      full_name?: string;
      phone?: string;
      organization_id?: string | null;
      roles?: string[];
      position?: string;
      area?: string;
      cohort_ids?: string[];
      new_password?: string;
    }) => {
      const { data, error } = await invokeManageUsers({ action: 'update_user', ...userData });
      if (error) {
        // Extract the real error message from the function response body.
        // supabase-js wraps non-2xx responses in a FunctionsHttpError whose
        // context is the raw Response; reading text() is the only universal path.
        let serverMsg = error.message;
        try {
          const ctx: any = (error as any).context;
          if (ctx?.body) {
            const parsed = typeof ctx.body === 'string' ? JSON.parse(ctx.body) : ctx.body;
            if (parsed?.error) serverMsg = parsed.error;
          } else if (typeof ctx?.json === 'function') {
            const parsed = await ctx.json();
            if (parsed?.error) serverMsg = parsed.error;
          } else if (typeof ctx?.text === 'function') {
            const txt = await ctx.text();
            try {
              const parsed = JSON.parse(txt);
              if (parsed?.error) serverMsg = parsed.error;
            } catch { if (txt) serverMsg = txt; }
          }
        } catch (_) { /* ignore parse errors */ }
        throw new Error(serverMsg);
      }
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['managed-users'] });
      queryClient.invalidateQueries({ queryKey: ['audit-logs'] });
      toast({ title: 'Usuario actualizado', description: 'Los datos del usuario han sido actualizados' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error al actualizar', description: error.message, variant: 'destructive' });
    },
  });

  // === TOGGLE ENTERPRISE LICENSE MUTATION ===
  const toggleEnterpriseLicenseMutation = useMutation({
    mutationFn: async ({ user_id, enable }: { user_id: string; enable: boolean }) => {
      const { data, error } = await invokeManageUsers({ action: 'toggle_enterprise_license', user_id, enable });
      if (error) {
        let serverMsg = error.message;
        try {
          const ctx: any = (error as any).context;
          if (ctx?.body) {
            const parsed = typeof ctx.body === 'string' ? JSON.parse(ctx.body) : ctx.body;
            if (parsed?.error) serverMsg = parsed.error;
          } else if (typeof ctx?.json === 'function') {
            const parsed = await ctx.json();
            if (parsed?.error) serverMsg = parsed.error;
          } else if (typeof ctx?.text === 'function') {
            const txt = await ctx.text();
            try {
              const parsed = JSON.parse(txt);
              if (parsed?.error) serverMsg = parsed.error;
            } catch { if (txt) serverMsg = txt; }
          }
        } catch { /* ignore */ }
        throw new Error(serverMsg);
      }
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['managed-users'] });
      queryClient.invalidateQueries({ queryKey: ['audit-logs'] });
      queryClient.invalidateQueries({ queryKey: ['org-enterprise-counts'] });
      // Refresh access/content caches so the new license state is reflected without reload
      queryClient.invalidateQueries({ queryKey: ['user-access'] });
      queryClient.invalidateQueries({ queryKey: ['all-content-by-category'] });
      queryClient.invalidateQueries({ queryKey: ['chapters-with-content'] });
      toast({
        title: variables.enable ? 'Licencia Enterprise activada' : 'Licencia Enterprise desactivada',
      });
    },
    onError: (error: Error) => {
      toast({ title: 'Error al cambiar licencia', description: error.message, variant: 'destructive' });
    },
  });

  // === TOGGLE PRO LICENSE (Super Admin courtesy) ===
  const toggleProLicenseMutation = useMutation({
    mutationFn: async ({ user_id, enable, reason, start_date, end_date }: { user_id: string; enable: boolean; reason?: string; start_date?: string | null; end_date?: string | null }) => {
      const { data, error } = await invokeManageUsers({ action: 'toggle_pro_license', user_id, enable, reason, start_date, end_date });
      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['managed-users'] });
      queryClient.invalidateQueries({ queryKey: ['audit-logs'] });
      queryClient.invalidateQueries({ queryKey: ['user-access'] });
      queryClient.invalidateQueries({ queryKey: ['user_active_licenses'] });
      queryClient.invalidateQueries({ queryKey: ['check_subscription'] });
      toast({
        title: variables.enable ? 'Licencia Pro de cortesía activada' : 'Licencia Pro de cortesía revocada',
      });
    },
    onError: (error: Error) => {
      toast({ title: 'Error al cambiar licencia Pro', description: error.message, variant: 'destructive' });
    },
  });

  // === BULK TOGGLE PRO LICENSE ===
  const bulkToggleProLicenseMutation = useMutation({
    mutationFn: async ({ user_ids, enable, reason, start_date, end_date }: { user_ids: string[]; enable: boolean; reason?: string; start_date?: string | null; end_date?: string | null }) => {
      const { data, error } = await invokeManageUsers({ action: 'bulk_toggle_pro_license', user_ids, enable, reason, start_date, end_date });
      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);
      return data as { ok_count: number; failed_count: number; failed: { user_id: string; error: string }[] };
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['managed-users'] });
      queryClient.invalidateQueries({ queryKey: ['audit-logs'] });
      queryClient.invalidateQueries({ queryKey: ['user-access'] });
      const action = variables.enable ? 'asignadas' : 'removidas';
      toast({
        title: `Licencias Pro ${action}`,
        description: `${data.ok_count} usuario(s) procesados${data.failed_count ? `, ${data.failed_count} fallaron` : ''}`,
        variant: data.failed_count > 0 ? 'destructive' : 'default',
      });
    },
    onError: (error: Error) => {
      toast({ title: 'Error en operación masiva', description: error.message, variant: 'destructive' });
    },
  });


  // Asignar / quitar empresa a varios usuarios (solo super_admin). organization_id null = quitar.
  const bulkSetOrganizationMutation = useMutation({
    mutationFn: async ({ user_ids, organization_id }: { user_ids: string[]; organization_id: string | null }) => {
      const { data, error } = await invokeManageUsers({ action: 'bulk_set_organization', user_ids, organization_id });
      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);
      return data as { ok_count: number; failed_count: number; failed: { user_id: string; error: string }[] };
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['managed-users'] });
      queryClient.invalidateQueries({ queryKey: ['audit-logs'] });
      queryClient.invalidateQueries({ queryKey: ['user-access'] });
      toast({
        title: variables.organization_id ? 'Empresa asignada' : 'Empresa quitada',
        description: `${data.ok_count} usuario(s) actualizados${data.failed_count ? `, ${data.failed_count} fallaron` : ''}`,
        variant: data.failed_count > 0 ? 'destructive' : 'default',
      });
    },
    onError: (error: Error) => {
      toast({ title: 'Error en operación masiva', description: error.message, variant: 'destructive' });
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: async (user_id: string) => {
      const { data, error } = await invokeManageUsers({ action: 'delete_user', user_id });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['managed-users'] });
      queryClient.invalidateQueries({ queryKey: ['audit-logs'] });
      toast({ title: 'Usuario eliminado', description: 'El usuario ha sido eliminado permanentemente' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error al eliminar usuario', description: error.message, variant: 'destructive' });
    },
  });

  return {
    // Queries
    users: usersWithLastLogin,
    isLoadingUsers: usersQuery.isLoading,
    isLoadingLastLogins: lastLoginsQuery.isLoading,
    organizations: organizationsQuery.data || [],
    cohorts: cohortsQuery.data || [],
    
    businessAreas: businessAreasQuery.data || [],
    // Mutations
    createUser: createUserMutation.mutateAsync,
    isCreatingUser: createUserMutation.isPending,
    batchCreate: batchCreateMutation.mutateAsync,
    isBatchCreating: batchCreateMutation.isPending,
    updateStatus: updateStatusMutation.mutateAsync,
    updateRole: updateRoleMutation.mutateAsync,
    updateUser: updateUserMutation.mutateAsync,
    isUpdatingUser: updateUserMutation.isPending,
    toggleEnterpriseLicense: toggleEnterpriseLicenseMutation.mutateAsync,
    isTogglingLicense: toggleEnterpriseLicenseMutation.isPending,
    toggleProLicense: toggleProLicenseMutation.mutateAsync,
    isTogglingProLicense: toggleProLicenseMutation.isPending,
    bulkToggleProLicense: bulkToggleProLicenseMutation.mutateAsync,
    isBulkTogglingProLicense: bulkToggleProLicenseMutation.isPending,
    bulkSetOrganization: bulkSetOrganizationMutation.mutateAsync,
    isBulkSettingOrganization: bulkSetOrganizationMutation.isPending,
    deleteUser: deleteUserMutation.mutateAsync,
    isDeletingUser: deleteUserMutation.isPending,
    // Context
    isSuperAdmin,
    isAdmin,
    callerOrgId: profile?.organization_id || null,
  };
}
